import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Button } from "@progress/kendo-react-buttons";
import {
  Checkbox,
  Input,
  NumericTextBox,
  RadioButton,
  TextArea,
} from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useDealReward } from "src/dm/hooks/useDealRewards";
import {
  useAddDealReward,
  usePromoteRewardItemsToMandatory,
  useRewardDefaults,
  useUpdateDealReward,
} from "src/dm/hooks/useDealRewardSub";
import { useCurrentUser } from "src/dm/auth";
import { useDealContext } from "src/dm/components/DealLayout/DealLayout";
import { useMarketingMessagesWithFlags } from "src/dm/hooks/useMarketingMessagesWithFlags";
import { useCatMgrs, usePromoInfo } from "src/dm/hooks/useLookups";
import {
  useRewardBuyQtyCodes,
  useRewardGetQtyCodes,
} from "src/dm/hooks/useRewardQtyCodes";
import { DealRewardWriteRequest } from "src/dm/interfaces/DealRewardSub";
import * as s from "./EditReward.css";

// Legacy EditReward.aspx is a five-panel form: Header / Vendor Liability /
// Details (Customer Saves + Ad Block + Tag Information nested) /. Vendor
// Liability is hidden for internal users on external-vendor deals (legacy
// `pnlVendorLiability.Visible = IsExternalUser`); Details is hidden for
// external users (`pnlInternalData.Visible = IsInternalUser`). When the
// deal is NOT internal, vendor liability is force-shown regardless.
// Marketing message dropdown drives whether tag-quantity / tag-line fields
// are editable (legacy `SetMarketingMessageFields` 0/1/2/3 bitmask).
//
// DealReward is 1:1 with Deal (legacy `DealReward.GetByDealId` returns a
// single object). When the deal has no reward row yet we show a
// "Create Reward" CTA that POSTs a stub reward and re-loads. If the user
// arrives via the left-nav "Reward" link on a deal flagged
// `isRewardPromotion`, we still show the CTA explicitly rather than
// silently auto-creating — that way the action is auditable and the user
// understands why a DEAL_REWARD_PROMO row appears in the history.
//
// Layout (2026 cleanup): every section uses the canonical `.formGrid`
// (180px label / 1fr input) so labels/inputs line up vertically across
// all six sections — fixes the alignment drift that came from the prior
// per-field inline-row containers.

const emptyWrite = (): DealRewardWriteRequest => ({
  autoBilling: false,
  excludeFromAmp: false,
});

// Minimum-viable values so the POST succeeds against the PROMOTE trigger
// (VENDOR_FUNDING_AMT is NOT NULL; vendor net amt OR vendor get* required).
const stubReward = (): DealRewardWriteRequest => ({
  autoBilling: false,
  excludeFromAmp: false,
  anyEachCode: "A",
  vendorBuyQty: 0,
  vendorBuyQtyCode: "D",
  vendorNetAmount: 0,
  vendorFundingAmount: 0,
  vendorFundingCode: "D",
});

// Derive the reward-type label legacy displays in the Details panel
// (BOGO / Reward Amount Off / Reward at Group Level).
const rewardTypeFromPromo = (info?: {
  isBogo: boolean;
  isRewardAmountOff: boolean;
  isRewardPromo: boolean;
}): string => {
  if (!info) return "—";
  if (info.isBogo) return "BOGO";
  if (info.isRewardAmountOff) return "Reward Amount Off";
  if (info.isRewardPromo) return "Reward at Group Level";
  return "Standard";
};

/**
 * Two-column form row helper. Drops a right-aligned label and a wrapped
 * input cell into the parent `.formGrid` so every section stays aligned.
 * The wrapping `<Fragment>` is important — putting an extra div around
 * the label/input would break the grid columns.
 */
const FormRow = ({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: React.ReactNode;
  htmlFor?: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Fragment>
    <label className={s.fieldLabel} htmlFor={htmlFor}>
      {label}
      {hint ? <span className={s.muted}> {hint}</span> : null}
    </label>
    <div className={s.inputCell}>{children}</div>
  </Fragment>
);

const EditReward = () => {
  const navigate = useNavigate();
  const { dealId: dealIdParam } = useParams<{ dealId: string }>();
  const dealId = dealIdParam ? Number(dealIdParam) : undefined;

  const deal = useDealContext();
  const { data: user } = useCurrentUser();
  const isInternalUser = user?.isInternalUser ?? false;
  const isExternalUser = user?.isExternalUser ?? false;
  // Legacy: vendor liability shown if external user OR deal is not internal
  const showVendorLiability = isExternalUser || !deal.isInternalDeal;
  // Legacy: details panel only for internal users
  const showInternalDetails = isInternalUser;

  const { data, isLoading, isError, error, refetch } = useDealReward(dealId);
  const update = useUpdateDealReward(dealId ?? 0);
  const create = useAddDealReward(dealId ?? 0);
  const promote = usePromoteRewardItemsToMandatory(dealId ?? 0);
  const defaultsQ = useRewardDefaults(dealId);

  const buyCodesQ = useRewardBuyQtyCodes();
  const getCodesQ = useRewardGetQtyCodes();
  const marketingMessagesQ = useMarketingMessagesWithFlags();
  const catMgrsQ = useCatMgrs();
  const promoInfoQ = usePromoInfo(data?.promotionCode ?? undefined);

  const [draft, setDraft] = useState<DealRewardWriteRequest>(emptyWrite());

  useEffect(() => {
    if (!data) return;
    setDraft({
      promotionCode: data.promotionCode,
      description: data.description,
      anyEachCode: data.anyEachCode,
      autoBilling: data.autoBilling,
      scanQuantity: data.scanQuantity,
      rewardQuantity: data.rewardQuantity,
      rewardAmount: data.rewardAmount,
      rewardPercent: data.rewardPercent,
      dealLimit: data.dealLimit,
      chargeDepartmentCode: data.chargeDepartmentCode,
      miscellaneousAccountCode: data.miscellaneousAccountCode,
      tagBuyQty: data.tagBuyQty,
      tagGetQty: data.tagGetQty,
      tagProductDescriptionLine1: data.tagProductDescriptionLine1,
      tagProductDescriptionLine2: data.tagProductDescriptionLine2,
      marketingMessageCode: data.marketingMessageCode,
      vendorBuyQty: data.vendorBuyQty,
      vendorBuyQtyCode: data.vendorBuyQtyCode,
      vendorGetQty: data.vendorGetQty,
      vendorGetQtyCode: data.vendorGetQtyCode,
      vendorNetAmount: data.vendorNetAmount,
      vendorFundingAmount: data.vendorFundingAmount,
      vendorFundingCode: data.vendorFundingCode,
      excludeFromAmp: data.excludeFromAmp,
      adBlockId: data.adBlockId,
      adBlockLineOfBusinessCode: data.adBlockLineOfBusinessCode,
      adBlockCategoryManagerCode: data.adBlockCategoryManagerCode,
      adBlockAdComment: data.adBlockAdComment,
      adBlockCouponComment: data.adBlockCouponComment,
      adBlockMerchandisingComment: data.adBlockMerchandisingComment,
      adBlockStoreComment: data.adBlockStoreComment,
    });
  }, [data]);

  // One-time default for cat-mgr on brand-new reward (legacy
  // setDefaltSelection). Only fires if the field is currently empty AND
  // we have a default to apply.
  useEffect(() => {
    if (!data) return;
    if (
      draft.adBlockCategoryManagerCode == null &&
      defaultsQ.data?.categoryManagerCode != null
    ) {
      setDraft((p) => ({
        ...p,
        adBlockCategoryManagerCode: defaultsQ.data?.categoryManagerCode ?? null,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultsQ.data?.categoryManagerCode, data?.dealRewardId]);

  // Marketing-message-driven enable flags. Legacy `SetMarketingMessageFields`
  // sums a bitmask (1=ProductDescription required, 2=TagQuantity required)
  // and only enables tag-line / tag-qty inputs when the selected message
  // requires them.
  const mmFlags = useMemo(() => {
    const code = draft.marketingMessageCode;
    if (!code || !marketingMessagesQ.data)
      return { tagLinesEnabled: false, tagQtysEnabled: false };
    const row = marketingMessagesQ.data.find((m) => m.id === code);
    return {
      tagLinesEnabled: row?.requiresProductDescription ?? false,
      tagQtysEnabled: row?.requiresTagQuantity ?? false,
    };
  }, [draft.marketingMessageCode, marketingMessagesQ.data]);

  // When the marketing message changes, blank out the disabled fields so
  // the user can't save stale tag-qty / tag-line values that no longer
  // apply (mirrors legacy ToggleTag* helpers).
  useEffect(() => {
    setDraft((p) => ({
      ...p,
      tagBuyQty: mmFlags.tagQtysEnabled ? p.tagBuyQty : null,
      tagGetQty: mmFlags.tagQtysEnabled ? p.tagGetQty : null,
      tagProductDescriptionLine1: mmFlags.tagLinesEnabled
        ? p.tagProductDescriptionLine1
        : null,
      tagProductDescriptionLine2: mmFlags.tagLinesEnabled
        ? p.tagProductDescriptionLine2
        : null,
    }));
  }, [mmFlags.tagLinesEnabled, mmFlags.tagQtysEnabled]);

  if (dealId === undefined) {
    return <p>Invalid URL.</p>;
  }
  if (isLoading) return <p>Loading reward…</p>;
  if (isError) {
    return (
      <p className={s.errorText}>
        Could not load reward:{" "}
        {error instanceof Error ? error.message : "unknown"}
      </p>
    );
  }

  // No reward exists yet — show "Create Reward" CTA. (We chose explicit
  // CTA over auto-create on mount so that DEAL_REWARD_PROMO inserts are
  // auditable and the user knows why the reward row appeared.)
  if (!data) {
    const handleCreate = async () => {
      try {
        await create.mutateAsync(stubReward());
        await refetch();
      } catch (e) {
        const status = (e as { response?: { status?: number } }).response
          ?.status;
        // 409 → another tab already created one; just refetch.
        if (status === 409) await refetch();
        else throw e;
      }
    };
    return (
      <div className={s.wrap}>
        <h2 className={s.title}>Reward</h2>
        {!deal.isRewardPromotion && (
          <p className={s.muted}>
            This deal is not flagged as a Reward Promotion. The reward form
            is normally only used when the <code>Reward Promotion</code>{" "}
            flag is set on the deal header.
          </p>
        )}
        <p>This deal does not have a reward configured yet.</p>
        <Button
          themeColor="primary"
          onClick={handleCreate}
          disabled={create.isPending}
        >
          {create.isPending ? "Creating…" : "Create Reward"}
        </Button>
        {create.isError && (
          <p className={s.errorText}>
            Failed to create reward:{" "}
            {create.error instanceof Error ? create.error.message : "unknown"}
          </p>
        )}
      </div>
    );
  }

  const posLocked = data.posPublished === true;

  const setField = <K extends keyof DealRewardWriteRequest>(
    key: K,
    value: DealRewardWriteRequest[K]
  ) => setDraft((p) => ({ ...p, [key]: value }));

  const handleSave = async () => {
    await update.mutateAsync(draft);
    navigate(`/dm/deals/${dealId}/reward`);
  };

  // Legacy For/Get mutual exclusion: pick one, blank the other. The
  // selected mode also controls which Vendor Liability inputs are
  // visually enabled / disabled.
  const forSelected =
    draft.vendorGetQty == null && draft.vendorGetQtyCode == null;
  const getSelected = !forSelected;

  const selectFor = () => {
    setDraft((p) => ({
      ...p,
      vendorGetQty: null,
      vendorGetQtyCode: null,
    }));
  };
  const selectGet = () => {
    // Set a sentinel non-null so forSelected flips. Use 0 / "U" (units)
    // by default — user will pick the real values.
    setDraft((p) => ({
      ...p,
      vendorGetQty: p.vendorGetQty ?? 0,
      vendorGetQtyCode: p.vendorGetQtyCode ?? "U",
      vendorNetAmount: null,
    }));
  };

  return (
    <div className={s.wrap}>
      <Link to={`/dm/deals/${dealId}/reward`} className={s.backLink}>
        ← Back to reward
      </Link>

      <div className={s.titleRow}>
        <h2 className={s.title}>
          Edit Reward
          {data.promotionId ? (
            <span className={s.subtitle}> (Prom Id {data.promotionId})</span>
          ) : null}
        </h2>
        <div className={s.titleActions}>
          <Link to={`/dm/deals/${dealId}/reward/items`}>
            <Button>Reward Items</Button>
          </Link>
          {data.autoBilling && (
            <Link to={`/dm/deals/${dealId}/reward/billing`}>
              <Button>Reward Billing</Button>
            </Link>
          )}
          <Link to={`/dm/deals/${dealId}/reward/view`}>
            <Button>View</Button>
          </Link>
        </div>
      </div>

      {/* Yellow advisory banner — POS Maintenance generated, edit locked. */}
      {posLocked && (
        <div className={s.posBanner}>
          POS Maintenance has been generated for this reward. It cannot be
          edited or deleted. Contact Deal Central Support for assistance.
        </div>
      )}

      {/* Header */}
      <section className={s.card}>
        <h3 className={s.sectionTitle}>Reward Header</h3>
        <div className={s.formGrid}>
          <FormRow label="Reward Description (30)" htmlFor="rwd-desc">
            <Input
              id="rwd-desc"
              maxLength={30}
              style={{ width: "100%" }}
              value={draft.description ?? ""}
              disabled={posLocked}
              onChange={(e) =>
                setField("description", String(e.target.value ?? "") || null)
              }
            />
          </FormRow>
          <FormRow label="Prom Id">
            <Input
              disabled
              style={{ width: "100%" }}
              value={data.promotionId != null ? String(data.promotionId) : "—"}
            />
          </FormRow>
        </div>
      </section>

      {/* Vendor Liability — external user OR external-vendor deal */}
      {showVendorLiability && (
        <section className={s.card}>
          <h3 className={s.sectionTitle}>Vendor Liability Statement</h3>
          {/*
            Audit 23-parity-deal-with-reward §2 / §8 #4 — the legacy
            EditReward form has TWO "amount" fields (one customer-facing,
            one vendor-funding) and the SPA's labels were ambiguous enough
            that test scripts and real users both forgot to fill the second
            one. The radio group below is the SINGLE source of truth for
            For-vs-Get mode and visibly disables the dependent halves so
            you can't accidentally fill the wrong path.
          */}
          <div className={s.modeToggle}>
            <span className={s.modeToggleLabel}>Vendor funds:</span>
            <RadioButton
              name="vendorFundMode"
              label="by amount (Vendor Net Reward)"
              checked={forSelected}
              onChange={selectFor}
              disabled={posLocked}
            />
            <RadioButton
              name="vendorFundMode"
              label="by quantity (Buy / Get)"
              checked={getSelected}
              onChange={selectGet}
              disabled={posLocked}
            />
          </div>

          <div className={s.formGrid}>
            <FormRow label="Vendor Buy Qty">
              <NumericTextBox
                format="n2"
                width="100%"
                value={draft.vendorBuyQty ?? null}
                disabled={posLocked || forSelected}
                onChange={(e) => setField("vendorBuyQty", e.value ?? null)}
              />
              <DropDownList
                data={buyCodesQ.data ?? []}
                textField="description"
                dataItemKey="id"
                value={
                  buyCodesQ.data?.find(
                    (c) => c.id === draft.vendorBuyQtyCode
                  ) ?? null
                }
                defaultItem={{ id: "", description: "-Select-" }}
                disabled={posLocked || forSelected}
                onChange={(e) =>
                  setField("vendorBuyQtyCode", e.value?.id || null)
                }
                style={{ minWidth: 160 }}
              />
            </FormRow>

            {/* For-mode row: Vendor Net Reward amount. Greyed when GET active. */}
            <FormRow label="For ($) — Vendor Net Reward">
              <div
                className={`${s.inputCell} ${getSelected ? s.disabledHalf : ""}`}
              >
                <NumericTextBox
                  format="c2"
                  width="100%"
                  value={draft.vendorNetAmount ?? null}
                  disabled={posLocked || !forSelected}
                  onChange={(e) =>
                    setField("vendorNetAmount", e.value ?? null)
                  }
                />
              </div>
            </FormRow>

            {/* Get-mode row: Vendor Get Qty + code. Greyed when FOR active. */}
            <FormRow label="Get — Vendor Get Qty">
              <div
                className={`${s.inputCell} ${forSelected ? s.disabledHalf : ""}`}
              >
                <NumericTextBox
                  format="n2"
                  width="100%"
                  value={draft.vendorGetQty ?? null}
                  disabled={posLocked || !getSelected}
                  onChange={(e) => setField("vendorGetQty", e.value ?? null)}
                />
                <DropDownList
                  data={getCodesQ.data ?? []}
                  textField="description"
                  dataItemKey="id"
                  value={
                    getCodesQ.data?.find(
                      (c) => c.id === draft.vendorGetQtyCode
                    ) ?? null
                  }
                  defaultItem={{ id: "", description: "-Select-" }}
                  disabled={posLocked || !getSelected}
                  onChange={(e) =>
                    setField("vendorGetQtyCode", e.value?.id || null)
                  }
                  style={{ minWidth: 160 }}
                />
              </div>
            </FormRow>

            <FormRow label="Vendor Agrees to Pay">
              <NumericTextBox
                format="n2"
                width={140}
                value={draft.vendorFundingAmount ?? null}
                disabled={posLocked}
                onChange={(e) =>
                  setField("vendorFundingAmount", e.value ?? null)
                }
              />
              <RadioButton
                name="fundCd"
                label="$"
                checked={draft.vendorFundingCode === "D"}
                disabled={posLocked}
                onChange={() => setField("vendorFundingCode", "D")}
              />
              <RadioButton
                name="fundCd"
                label="%"
                checked={draft.vendorFundingCode === "P"}
                disabled={posLocked}
                onChange={() => setField("vendorFundingCode", "P")}
              />
              <span className={s.muted}>per reward</span>
            </FormRow>
          </div>
        </section>
      )}

      {/* Details — internal users only */}
      {showInternalDetails && (
        <section className={s.card}>
          <h3 className={s.sectionTitle}>Details</h3>
          <div className={s.formGrid}>
            <FormRow label="Promo Code">
              <NumericTextBox
                width="100%"
                value={draft.promotionCode ?? null}
                disabled={posLocked}
                onChange={(e) => setField("promotionCode", e.value ?? null)}
              />
            </FormRow>
            <FormRow label="Reward Type">
              <Input
                disabled
                style={{ width: "100%" }}
                value={rewardTypeFromPromo(promoInfoQ.data)}
              />
            </FormRow>
            <FormRow label="Mandatory Purchase Scan Qty">
              <NumericTextBox
                width={140}
                value={draft.scanQuantity ?? null}
                disabled={posLocked}
                onChange={(e) => setField("scanQuantity", e.value ?? null)}
              />
              <RadioButton
                name="anyEach"
                label="Any"
                checked={draft.anyEachCode === "A"}
                disabled={posLocked}
                onChange={() => setField("anyEachCode", "A")}
              />
              <RadioButton
                name="anyEach"
                label="Each"
                checked={draft.anyEachCode === "E"}
                disabled={posLocked}
                onChange={() => setField("anyEachCode", "E")}
              />
            </FormRow>
            <FormRow label="Limit">
              <NumericTextBox
                width={140}
                value={draft.dealLimit ?? null}
                disabled={posLocked}
                onChange={(e) => setField("dealLimit", e.value ?? null)}
              />
            </FormRow>
            <FormRow label="Charge Dept">
              <NumericTextBox
                width={140}
                value={draft.chargeDepartmentCode ?? null}
                disabled={posLocked}
                onChange={(e) =>
                  setField("chargeDepartmentCode", e.value ?? null)
                }
              />
            </FormRow>
            <FormRow label="Misc Account Code">
              <NumericTextBox
                width={140}
                value={draft.miscellaneousAccountCode ?? null}
                disabled={posLocked}
                onChange={(e) =>
                  setField("miscellaneousAccountCode", e.value ?? null)
                }
              />
            </FormRow>
          </div>
        </section>
      )}

      {/* Customer Saves — promoted to its own card to match the spec
          ordering (Header / Vendor Liability / Customer Saves / Ad Block /
          Tag Information). Legacy nested this inside Details but the
          fields are equally relevant to external users. */}
      {showInternalDetails && (
        <section className={s.card}>
          <h3 className={s.sectionTitle}>Customer Saves</h3>
          <div className={s.formGrid}>
            <FormRow label="Reward Item Qty">
              <NumericTextBox
                width={140}
                value={draft.rewardQuantity ?? null}
                disabled={posLocked}
                onChange={(e) => setField("rewardQuantity", e.value ?? null)}
              />
            </FormRow>
            <FormRow
              label="Customer Reward Amount"
              hint="(customer-facing $ off)"
            >
              <NumericTextBox
                format="c2"
                width={180}
                value={draft.rewardAmount ?? null}
                disabled={posLocked}
                onChange={(e) => setField("rewardAmount", e.value ?? null)}
              />
            </FormRow>
            <FormRow label="Reward Percent Off">
              <NumericTextBox
                format="n2"
                width={140}
                value={draft.rewardPercent ?? null}
                disabled={posLocked}
                onChange={(e) => setField("rewardPercent", e.value ?? null)}
              />
            </FormRow>
            <FormRow label="">
              <Checkbox
                label="Automated Billing"
                checked={draft.autoBilling}
                disabled={posLocked}
                onChange={(e) => setField("autoBilling", !!e.value)}
              />
            </FormRow>
          </div>
        </section>
      )}

      {/* Ad Block Information */}
      {showInternalDetails && (
        <section className={s.card}>
          <h3 className={s.sectionTitle}>Ad Block Information</h3>
          <div className={s.formGrid}>
            <FormRow label="">
              <Checkbox
                label="Do Not Create Reward Ad Block"
                checked={draft.excludeFromAmp}
                disabled={posLocked}
                onChange={(e) => setField("excludeFromAmp", !!e.value)}
              />
            </FormRow>
            <FormRow label="Ad Block ID">
              <Input
                disabled
                style={{ width: 200 }}
                value={data.adBlockId != null ? String(data.adBlockId) : "—"}
              />
            </FormRow>
            <FormRow label="Line of Business">
              <Input
                style={{ width: "100%" }}
                value={draft.adBlockLineOfBusinessCode ?? ""}
                disabled={posLocked || draft.excludeFromAmp}
                onChange={(e) =>
                  setField(
                    "adBlockLineOfBusinessCode",
                    String(e.target.value ?? "") || null
                  )
                }
              />
            </FormRow>
            <FormRow label="Category Manager">
              <DropDownList
                style={{ width: "100%" }}
                data={catMgrsQ.data ?? []}
                textField="name"
                dataItemKey="code"
                value={
                  catMgrsQ.data?.find(
                    (c) => Number(c.code) === draft.adBlockCategoryManagerCode
                  ) ?? null
                }
                defaultItem={{ code: "", name: "-Select-" }}
                disabled={posLocked || draft.excludeFromAmp}
                onChange={(e) =>
                  setField(
                    "adBlockCategoryManagerCode",
                    e.value?.code ? Number(e.value.code) : null
                  )
                }
              />
            </FormRow>
            <FormRow label="Coupon Comment (200)">
              <TextArea
                maxLength={200}
                rows={3}
                style={{ width: "100%" }}
                value={draft.adBlockCouponComment ?? ""}
                disabled={posLocked || draft.excludeFromAmp}
                onChange={(e) =>
                  setField(
                    "adBlockCouponComment",
                    String(e.value ?? "") || null
                  )
                }
              />
            </FormRow>
            <FormRow label="Store Comment (400)">
              <TextArea
                maxLength={400}
                rows={3}
                style={{ width: "100%" }}
                value={draft.adBlockStoreComment ?? ""}
                disabled={posLocked || draft.excludeFromAmp}
                onChange={(e) =>
                  setField(
                    "adBlockStoreComment",
                    String(e.value ?? "") || null
                  )
                }
              />
            </FormRow>
            <FormRow label="Merch Comment (400)">
              <TextArea
                maxLength={400}
                rows={3}
                style={{ width: "100%" }}
                value={draft.adBlockMerchandisingComment ?? ""}
                disabled={posLocked || draft.excludeFromAmp}
                onChange={(e) =>
                  setField(
                    "adBlockMerchandisingComment",
                    String(e.value ?? "") || null
                  )
                }
              />
            </FormRow>
            <FormRow label="Ad Comment (200)">
              <TextArea
                maxLength={200}
                rows={3}
                style={{ width: "100%" }}
                value={draft.adBlockAdComment ?? ""}
                disabled={posLocked || draft.excludeFromAmp}
                onChange={(e) =>
                  setField("adBlockAdComment", String(e.value ?? "") || null)
                }
              />
            </FormRow>
          </div>
        </section>
      )}

      {/* Tag Information */}
      {showInternalDetails && (
        <section className={s.card}>
          <h3 className={s.sectionTitle}>Tag Information</h3>
          <div className={s.formGrid}>
            <FormRow label="Marketing Message">
              <DropDownList
                style={{ width: "100%" }}
                data={marketingMessagesQ.data ?? []}
                textField="description"
                dataItemKey="id"
                value={
                  marketingMessagesQ.data?.find(
                    (m) => m.id === draft.marketingMessageCode
                  ) ?? null
                }
                defaultItem={{
                  id: "",
                  description: "-Select-",
                  requiresProductDescription: false,
                  requiresTagQuantity: false,
                }}
                disabled={posLocked}
                onChange={(e) =>
                  setField("marketingMessageCode", e.value?.id || null)
                }
              />
            </FormRow>
            <FormRow
              label="Buy Quantity"
              hint={!mmFlags.tagQtysEnabled ? "(n/a)" : undefined}
            >
              <NumericTextBox
                width={140}
                value={draft.tagBuyQty ?? null}
                disabled={posLocked || !mmFlags.tagQtysEnabled}
                onChange={(e) => setField("tagBuyQty", e.value ?? null)}
              />
            </FormRow>
            <FormRow
              label="Get Quantity"
              hint={!mmFlags.tagQtysEnabled ? "(n/a)" : undefined}
            >
              <NumericTextBox
                width={140}
                value={draft.tagGetQty ?? null}
                disabled={posLocked || !mmFlags.tagQtysEnabled}
                onChange={(e) => setField("tagGetQty", e.value ?? null)}
              />
            </FormRow>
            <FormRow
              label="Tag Line 1 (18)"
              hint={!mmFlags.tagLinesEnabled ? "(n/a)" : undefined}
            >
              <Input
                maxLength={18}
                style={{ width: "100%" }}
                value={draft.tagProductDescriptionLine1 ?? ""}
                disabled={posLocked || !mmFlags.tagLinesEnabled}
                onChange={(e) =>
                  setField(
                    "tagProductDescriptionLine1",
                    String(e.target.value ?? "") || null
                  )
                }
              />
            </FormRow>
            <FormRow
              label="Tag Line 2 (25)"
              hint={!mmFlags.tagLinesEnabled ? "(n/a)" : undefined}
            >
              <Input
                maxLength={25}
                style={{ width: "100%" }}
                value={draft.tagProductDescriptionLine2 ?? ""}
                disabled={posLocked || !mmFlags.tagLinesEnabled}
                onChange={(e) =>
                  setField(
                    "tagProductDescriptionLine2",
                    String(e.target.value ?? "") || null
                  )
                }
              />
            </FormRow>
          </div>
        </section>
      )}

      {/* Right-aligned action bar — Save / Cancel / Promote → mandatory
          / View Items / View Billing. Legacy used a single floating-right
          Save button; we add the contextual nav buttons here so all
          page-level actions live in one place. */}
      <div className={s.actions}>
        <Link to={`/dm/deals/${dealId}/reward/items`}>
          <Button type="button">View Items</Button>
        </Link>
        {data.autoBilling && (
          <Link to={`/dm/deals/${dealId}/reward/billing`}>
            <Button type="button">View Billing</Button>
          </Link>
        )}
        <Button
          type="button"
          onClick={() => {
            if (
              confirm(
                "Promote every item on this reward to mandatory (ITM_TYP_CD=M)?"
              )
            ) {
              promote.mutate(undefined, {
                onSuccess: (res) =>
                  alert(`Promoted ${res.promoted} reward item(s) to mandatory.`),
                onError: (e) =>
                  alert(
                    `Promote failed: ${e instanceof Error ? e.message : "unknown"}`
                  ),
              });
            }
          }}
          disabled={posLocked || promote.isPending}
          title="Set every reward item on this reward to the mandatory item type."
        >
          {promote.isPending ? "Promoting…" : "Promote → mandatory"}
        </Button>
        <Link to={`/dm/deals/${dealId}/reward/view`}>
          <Button type="button">Cancel</Button>
        </Link>
        <Button
          type="button"
          themeColor="primary"
          onClick={handleSave}
          disabled={posLocked || update.isPending}
        >
          {update.isPending ? "Saving…" : "Save reward"}
        </Button>
      </div>
      {update.isError && (
        <p className={s.errorText}>
          Failed to save:{" "}
          {update.error instanceof Error ? update.error.message : "unknown"}
        </p>
      )}
    </div>
  );
};

export default EditReward;
